import React, {useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {LlmData} from '../../../../../common-lib/src/api/Api.types';
import llmImg from './images/llm.svg';
import arrowImg from './images/arrow.svg';
import checkImg from './images/check.svg';
import uncheckImg from './images/uncheck.svg';
import lockImg from './images/lock.svg';
import styles from './LlmSelector.module.css';
import {DoubleSwitcher} from '../../../../../common-lib/src/components/DoubleSwitcher/DoubleSwitcher';

type Props = {
	textLlms: LlmData[];
	photoLlms: LlmData[];
	selectedTextLlm: LlmData | null;
	selectedPhotoLlm: LlmData | null;
	handleSelectLlm: (llm: LlmData | null, type: 'text' | 'photo') => void;
};

export const LlmSelector = ({
	textLlms,
	photoLlms,
	selectedTextLlm,
	selectedPhotoLlm,
	handleSelectLlm,
}: Props) => {
	const [opened, setOpened] = useState(false);
	const [isText, setIsText] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isBottom, setIsBottom] = useState(false);

	const handleCloseLlmSelector = (e: MouseEvent) => {
		if (
			!(e.target as HTMLElement)?.closest('.actions-row__llm') &&
			!(e.target as HTMLElement)?.classList?.contains('llm-selector__row') &&
			!(e.target as HTMLElement)?.closest('#llm-selector-container')
		) {
			setOpened(false);
		}
	};

	useEffect(() => {
		if (opened) {
			document.addEventListener('click', handleCloseLlmSelector);
		} else {
			document.removeEventListener('click', handleCloseLlmSelector);
		}
		return () => {
			document.removeEventListener('click', handleCloseLlmSelector);
		};
	}, [opened]);

	useEffect(() => {
		if (!containerRef.current || !opened) {
			setIsBottom(false);
			return;
		}

		const rect = containerRef.current.getBoundingClientRect();
		setIsBottom(rect.y < 0);
	}, [opened]);

	if (!textLlms?.length && !photoLlms?.length) {
		return null;
	}

	const defaultTextLlm = textLlms.find((llm) => llm.available);
	const defaultPhotoLlm = photoLlms.find((llm) => llm.available);
	const internalSelectedTextLlm = selectedTextLlm || defaultTextLlm;
	const internalSelectedPhotoLlm = selectedPhotoLlm || defaultPhotoLlm;
	const isSelectedTextBasic = internalSelectedTextLlm?.access_level === 'basic';
	const textLlmName =
		internalSelectedTextLlm?.name ??
		(isSelectedTextBasic ? 'Basic' : internalSelectedTextLlm?.name);
	const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (!(e.target as HTMLElement)?.closest('#llm-selector-container')) {
			e.stopPropagation();
			setOpened(!opened);
		}
	};

	const handleSelect = (llm: LlmData | null, type: 'text' | 'photo') => {
		handleSelectLlm(llm, type);
	};

	const getLlmCheck = (llm: LlmData) => {
		if (
			llm.id === internalSelectedTextLlm?.id ||
			llm.id === internalSelectedPhotoLlm?.id
		) {
			return (
				<img src={checkImg} alt="check" className={styles.llmSelectorCheck} />
			);
		}

		if (!llm.available) {
			return (
				<img src={lockImg} alt="lock" className={styles.llmSelectorLock} />
			);
		}

		return (
			<img src={uncheckImg} alt="uncheck" className={styles.llmSelectorCheck} />
		);
	};

	return (
		<div
			className={cn('actions-row__llm', styles.actionsRowLlm)}
			onClick={handleClick}
		>
			<img src={llmImg} alt="LLM" />
			<p className={styles.actionsRowLlmName}>{textLlmName}</p>
			<img src={arrowImg} alt="open" className={styles.actionsRowLlmArrow} />
			{opened && (
				<div
					className={cn(styles.actionsRowLlmSelector, {
						[styles.actionsRowLlmSelectorBottom]: isBottom,
					})}
					ref={containerRef}
					id="llm-selector-container"
				>
					<div className={styles.llmSelectorRow}>
						<DoubleSwitcher
							left="Text"
							right="Images"
							isLeft={isText}
							onSwitch={(isLeft) => {
								setIsText(isLeft);
							}}
							className={styles.switcher}
							id="llm-selector-switcher"
						/>
					</div>
					{(isText ? textLlms : photoLlms).map((llm) => (
						<div
							className={cn(styles.llmSelectorRow, 'llm-selector__row')}
							onClick={(e) => {
								e.stopPropagation();
								handleSelect(llm, isText ? 'text' : 'photo');
							}}
							key={llm.id}
						>
							<img
								src={llm.logo_url}
								alt={llm.name}
								className={styles.llmSelectorImg}
							/>
							<div className={styles.llmSelectorTextWrapper}>
								<p className={styles.llmSelectorText}>{llm.name}</p>
								<p className={styles.llmSelectorDescription}>
									{llm.description}
								</p>
							</div>
							{getLlmCheck(llm)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
